import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, TrendingUp, Users, DollarSign, Target, Briefcase, HelpCircle } from "lucide-react";
import SeoHead from "@/components/shared/SeoHead";
import PageHero, { HeroStatTile } from "@/components/shared/PageHero";
import AdSlot from "@/components/shared/AdSlot";
import AdFrame from "@/components/shared/AdFrame";
import SalaryTable from "@/components/shared/SalaryTable";
import RppToggle from "@/components/shared/RppToggle";
import PercentileChart from "@/components/shared/PercentileChart";
import AffiliateCta from "@/components/shared/AffiliateCta";
import OccupationLink from "@/components/shared/OccupationLink";
import MetricNavigation from "@/components/shared/MetricNavigation";
import RelatedMetrics from "@/components/shared/RelatedMetrics";
import AIRiskPreview from "@/components/shared/AIRiskPreview";
import OccupationEnrichment from "@/components/shared/OccupationEnrichment";
import DataConfidenceBadge from "@/components/shared/DataConfidenceBadge";
import RealWageInsight from "@/components/shared/RealWageInsight";
import FinancialStatusInsight from "@/components/shared/FinancialStatusInsight";
import SimilarCareers from "@/components/shared/SimilarCareers";
import OccupationIntro from "@/components/content/OccupationIntro";
import WhatDoTheyDo from "@/components/content/WhatDoTheyDo";
import LocationContext from "@/components/content/LocationContext";
import SalaryPageInsight from "@/components/content/SalaryPageInsight";
import CostOfLivingNote from "@/components/content/CostOfLivingNote";
import StaticOccupationContent from "@/components/content/StaticOccupationContent";
import StateVsNationCompare from "@/components/content/StateVsNationCompare";
import DemandSignalBlock from "@/components/shared/DemandSignalBlock";
import RelatedOccupations from "@/components/seo/RelatedOccupations";
import RelatedStates from "@/components/seo/RelatedStates";
import NewsletterSignup from "@/components/shared/NewsletterSignup";
import OccupationHubLinks from "@/components/seo/OccupationHubLinks";
import { supabase } from "@/integrations/supabase/client";
import { useOccupationResolver } from "@/hooks/useOccupationResolver";
import { useCareerRecommendations } from "@/hooks/useCareerRecommendations";
import { useOccupationSalaryData } from "@/hooks/useOccupationSalaryData";
import { formatLocationName, toTitleCase } from "@/lib/utils";
import { isValidState, getAreaCodeForLocation } from "@/lib/stateUtils";
import { shouldIndexSalaryPage } from "@/lib/seoIndexing";
import { useAreaCodeResolver } from "@/hooks/useAreaCodeResolver";
import { generateFAQPageSchema, generateMultipleSchemas, generateOrganizationSchema, generateOccupationSchema, generateWebPageSchema } from "@/lib/schema";
import { getSalarySnapshot, getSocMap, type SocMapEntry } from "@/lib/pageDataSnapshot";

import NotFound from "./NotFound";

const OccupationSalaryCenter = () => {
  const { occupation, location } = useParams<{ occupation: string; location: string }>();
  const navigate = useNavigate();
  const [useRpp, setUseRpp] = useState(false);

  // Parse occupation parameter from URL slug
  const occupationSlug = occupation || '';
  const locationName = formatLocationName(location || '');

  // Handle navigation in useEffect to avoid React warnings
  useEffect(() => {
    // Check if the first parameter is actually a state (common user error)
    if (occupationSlug && isValidState(occupationSlug)) {
      // User likely meant to search for jobs in this state
      // Redirect to the best states page for this location
      navigate(`/best-states/${occupationSlug}`, { replace: true });
    }
  }, [occupationSlug, navigate]);

  // Don't render if we're redirecting due to state detection
  const shouldRedirect = occupationSlug && isValidState(occupationSlug);

  // Build-time SOC map: resolves slug → SOC synchronously (no API round-trip)
  const { data: socMapEntry } = useQuery<SocMapEntry | null>({
    queryKey: ['soc-map-entry', occupationSlug],
    queryFn: async () => {
      const map = await getSocMap();
      return map?.[occupationSlug] ?? null;
    },
    staleTime: Infinity,
  });

  const isNationalPage = location === 'united-states' || location === 'national';

  // National snapshot for state-vs-nation when live API omits national fields
  const { data: nationalCompare } = useQuery({
    queryKey: ['salary-national-compare', occupationSlug],
    enabled: !!occupationSlug && !isNationalPage,
    queryFn: () => getSalarySnapshot(occupationSlug, 'united-states'),
    staleTime: Infinity,
  });

  // Resolve occupation dynamically from database
  const { data: resolvedOccupation, isLoading: isResolvingOccupation, isError: isResolveError, error: resolveError } = useOccupationResolver(occupationSlug);

  // The build-time SOC map is authoritative for curated slugs: the live
  // resolver fuzzy-matches and can land on a different occupation entirely
  // (e.g. /salary/therapists/ -> "Marriage and Family Therapists"). Only use
  // the resolver's richer record when it agrees with the map (or no map
  // entry exists, i.e. non-curated slugs).
  const socMapOccupation = socMapEntry ? {
    soc: socMapEntry.soc,
    title: socMapEntry.title,
    description: undefined,
    median_salary: 0,
    employment: 0,
    no_degree_required: false,
  } : undefined;
  const resolverAgreesWithMap = !socMapEntry || resolvedOccupation?.soc === socMapEntry.soc;
  const currentOccupation = (resolverAgreesWithMap ? resolvedOccupation : undefined)
    ?? socMapOccupation;

  // Resolve area code for location (handles states, MSAs, and national)
  const { data: resolvedAreaCode, isLoading: isResolvingArea } = useAreaCodeResolver(location || '');
  const areaCode = resolvedAreaCode || (location === 'united-states' ? '99' : getAreaCodeForLocation(location || '') || '99');

  // Salary first — keyed by slug so snapshots load without waiting on SOC map /
  // api-occupations. Live API path still needs a SOC once the map resolves.
  const { data: salaryData, isLoading } = useQuery({
    queryKey: ['occupation-salary', occupationSlug, location, useRpp],
    queryFn: async () => {
      if (!useRpp && occupationSlug && location) {
        const snapshot = await getSalarySnapshot(occupationSlug, location);
        if (snapshot) return snapshot;
      }

      const soc = currentOccupation?.soc;
      if (!soc) {
        throw new Error('SOC not available for live salary fetch');
      }

      const { data, error } = await supabase.functions.invoke('api-salary', {
        body: {
          area: locationName,
          soc,
          rpp: useRpp
        }
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!location && !!occupationSlug,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData
  });

  // Occupation identity for content: resolver → soc-map → salary snapshot fields
  const occupationForContent = currentOccupation ?? (
    salaryData?.soc
      ? {
          soc: String(salaryData.soc),
          title: String(
            salaryData.occ_title ||
              occupationSlug
                .split('-')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ')
          ),
          description: undefined as string | undefined,
          median_salary: 0,
          employment: 0,
          no_degree_required: false,
        }
      : undefined
  );

  // Get career recommendations
  const { data: careerRecommendations = [], isLoading: isRecommendationsLoading } = useCareerRecommendations(
    occupationForContent?.soc || currentOccupation?.soc || '',
    areaCode,
    6
  );

  // Get salary data for recommended occupations
  const { data: recommendedSalaryData = [] } = useOccupationSalaryData(
    careerRecommendations.map(rec => rec.recommended_occupation_code),
    areaCode
  );

  // Get industry mix data
  const { data: industryData, isLoading: isLoadingIndustry } = useQuery({
    queryKey: ['industry-mix', occupationForContent?.soc || currentOccupation?.soc, location],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-industry-mix', {
        body: {
          soc_code: occupationForContent?.soc || currentOccupation?.soc,
          area_code: location === 'united-states' ? '99' : undefined,
          year: 2025
        }
      });
      
      if (error) throw error;
      return data?.industries || [];
    },
    enabled: !!location && !!(occupationForContent?.soc || currentOccupation?.soc)
  });

  // Get career insights data
  const { data: careerInsights } = useQuery({
    queryKey: ['career-insights', occupationForContent?.soc || currentOccupation?.soc, areaCode],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-career-insights', {
        body: {
          soc_code: occupationForContent?.soc || currentOccupation?.soc,
          area_code: areaCode,
          user_experience_level: 'professional'
        }
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!location && !!(occupationForContent?.soc || currentOccupation?.soc) && !!areaCode
  });

  // Helper to get mean salary with RPP adjustment
  const getMeanSalary = () => {
    if (!salaryData) return 0;
    return useRpp 
      ? (salaryData.real_annual_mean_wage || salaryData.annual_mean_wage || salaryData.mean_salary || 0)
      : (salaryData.annual_mean_wage || salaryData.mean_salary || 0);
  };

  // Helper to get median salary with RPP adjustment
  const getMedianSalary = () => {
    if (!salaryData) return 0;
    return useRpp 
      ? (salaryData.real_annual_median_wage || salaryData.annual_median_wage || salaryData.median_salary || 0)
      : (salaryData.annual_median_wage || salaryData.median_salary || 0);
  };

  // Use actual percentile data from API response - apply RPP adjustment if enabled
  // Note: API doesn't return RPP-adjusted percentiles, so we calculate from adjusted mean/median
  const percentileData = salaryData ? (() => {
    const meanSalary = getMeanSalary();
    const medianSalary = getMedianSalary();
    
    // If RPP is enabled, we need to apply the same adjustment ratio to percentiles
    // Calculate adjustment ratio from mean salary
    const rppAdjustment = useRpp && salaryData.annual_mean_wage && salaryData.real_annual_mean_wage
      ? salaryData.real_annual_mean_wage / salaryData.annual_mean_wage
      : 1;
    
    const getPercentile = (basePercentile: number | null, fallbackMultiplier: number) => {
      if (basePercentile !== null && basePercentile !== undefined) {
        return useRpp ? Math.round(basePercentile * rppAdjustment) : basePercentile;
      }
      return Math.round(meanSalary * fallbackMultiplier);
    };
    
    return [
      { 
        percentile: 10, 
        salary: getPercentile(salaryData.annual_10th_percentile_wage || salaryData.p10_salary, 0.6), 
        label: '10th Percentile' 
      },
      { 
        percentile: 25, 
        salary: getPercentile(salaryData.annual_25th_percentile_wage || salaryData.p25_salary, 0.75), 
        label: '25th Percentile' 
      },
      { 
        percentile: 50, 
        salary: useRpp ? medianSalary : (medianSalary || Math.round(meanSalary * 0.9)), 
        label: 'Median' 
      },
      { 
        percentile: 75, 
        salary: getPercentile(salaryData.annual_75th_percentile_wage || salaryData.p75_salary, 1.1), 
        label: '75th Percentile' 
      },
      { 
        percentile: 90, 
        salary: getPercentile(salaryData.annual_90th_percentile_wage || salaryData.p90_salary, 1.3), 
        label: '90th Percentile' 
      },
    ];
  })() : [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Helper functions for displaying recommendation data
  const formatSalaryImprovement = (improvement: number) => {
    const sign = improvement >= 0 ? '+' : '';
    return `${sign}${Math.round(improvement)}%`;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
      case 'accessible':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'difficult':
      case 'challenging':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'very difficult':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getSalaryImprovementColor = (improvement: number) => {
    if (improvement > 10) return 'text-green-600 dark:text-green-400';
    if (improvement > 0) return 'text-green-500 dark:text-green-300';
    if (improvement > -10) return 'text-orange-500 dark:text-orange-400';
    return 'text-red-500 dark:text-red-400';
  };

  // Generate title from URL params (always available, no API needed)
  const urlDerivedOccupationTitle = occupationSlug
    ? toTitleCase(occupationSlug.replace(/-/g, ' '))
    : 'Occupation';
  
  // Use API/map/snapshot title when available, fallback to URL-derived
  const occupationTitle = occupationForContent?.title || currentOccupation?.title || urlDerivedOccupationTitle;

  // Titles/descriptions target dominant query patterns ("average X salary",
  // "how much do X make", "X salary [state]") and include the page's own wage
  // figure so every curated URL has unique metadata.
  const metaYear = new Date().getFullYear();
  const meanForMeta = getMeanSalary();
  const medianForMeta = getMedianSalary();
  const pageTitle = meanForMeta > 0
    ? `${occupationTitle} Salary in ${locationName}: ${formatCurrency(meanForMeta)} Average (${metaYear}) | Wage Atlas`
    : `${occupationTitle} Average Salary in ${locationName} (${metaYear}) | Wage Atlas`;

  const pageDescription = meanForMeta > 0
    ? `How much do ${occupationTitle} make in ${locationName}? Average salary is ${formatCurrency(meanForMeta)} (median ${formatCurrency(medianForMeta)}). See pay percentiles, hourly rates, and top industries from official BLS OEWS data.`
    : `How much do ${occupationTitle} make in ${locationName}? View average and median salary, hourly rates, and percentile distributions from official BLS OEWS data.`;

  // Generate FAQ schema for AEO
  const baseUrl = 'https://wageatlas.com';
  const canonicalUrl = `${baseUrl}/salary/${occupationSlug}/${location?.toLowerCase().replace(/\s+/g, '-') || 'united-states'}/`;

  // Indexation is curated occupation + national/state only. Enrichment must
  // never gate this — see src/lib/seoIndexing.ts.
  const isIndexable = shouldIndexSalaryPage(occupationSlug, location);

  // Pass a 2-digit state FIPS to the layoffs feed; national pages get the
  // national feed (no filter).
  const enrichmentStateCode = areaCode && /^\d{2}$/.test(areaCode) && areaCode !== '99' ? areaCode : undefined;

  const salaryBoosters = careerInsights?.salary_boosters ?? [];
  const skillRecommendations = (careerInsights?.skills_recommendations ?? [])
    .flatMap((category: { category?: string; skills?: Array<{
      name: string;
      importance_score?: number;
      reason?: string;
      salary_impact?: string;
    }> }) =>
      (category.skills ?? []).map((skill) => ({
        ...skill,
        category: category.category,
      }))
    );
  const hasAdvancementContent =
    salaryBoosters.length > 0 || skillRecommendations.length > 0;
  
  const hourlyForMeta = useRpp
    ? (salaryData?.real_hourly_mean_wage || salaryData?.hourly_mean_wage || salaryData?.hourly_mean || 0)
    : (salaryData?.hourly_mean_wage || salaryData?.hourly_mean || 0);
  const employmentForMeta = salaryData?.total_employment || salaryData?.tot_emp || 0;
  const dataYear =
    salaryData?.year || salaryData?.oews_year || salaryData?.data_year || metaYear;

  // Static FAQs that work without API data
  const staticFaqItems = [
    {
      question: `How much do ${occupationTitle} make in ${locationName}?`,
      answer: `${occupationTitle} pay in ${locationName} varies by experience, industry, and role. This page reports median and mean wages, percentile distributions, and hourly rates from the Bureau of Labor Statistics OEWS survey.`,
    },
    {
      question: `What is the average ${occupationTitle} salary in ${locationName}?`,
      answer: `Average and median ${occupationTitle} salaries for ${locationName} are shown in the wage tables on this page, sourced from official BLS employer-reported data rather than self-reported surveys.`,
    },
    {
      question: `What is the job outlook for ${occupationTitle}?`,
      answer: `Job outlook for ${occupationTitle} varies by location and industry. See our trends page for historical employment and wage movement for this occupation in ${locationName}.`,
    },
  ];

  // Dynamic FAQs with API data (when available)
  const dynamicFaqItems = salaryData ? [
    {
      question: `How much do ${occupationTitle} make in ${locationName}?`,
      answer: `The average (mean) salary for ${occupationTitle} in ${locationName} is ${formatCurrency(getMeanSalary())} per year. The median salary is ${formatCurrency(getMedianSalary())}, meaning half of workers earn more and half earn less.`,
    },
    {
      question: `What is the average ${occupationTitle} salary in ${locationName}?`,
      answer: `${occupationTitle} in ${locationName} earn an average of ${formatCurrency(getMeanSalary())} annually (${formatCurrency(getMedianSalary())} median) according to the latest BLS OEWS release.`,
    },
    {
      question: `How much does ${occupationTitle} make per hour in ${locationName}?`,
      answer: `${occupationTitle} workers in ${locationName} earn approximately $${hourlyForMeta} per hour on average, based on official Bureau of Labor Statistics data.`,
    },
    {
      question: `What is the salary range for ${occupationTitle} in ${locationName}?`,
      answer: `The 10th percentile (entry-level) earns around ${formatCurrency(percentileData[0]?.salary || getMeanSalary() * 0.6)}, while the 90th percentile (experienced) earns around ${formatCurrency(percentileData[4]?.salary || getMeanSalary() * 1.3)}. The median salary is ${formatCurrency(getMedianSalary())}.`,
    },
    {
      question: `How many ${occupationTitle} jobs are there in ${locationName}?`,
      answer: `There are approximately ${formatNumber(employmentForMeta)} ${occupationTitle} positions in ${locationName}, according to the latest Bureau of Labor Statistics data.`,
    },
  ] : [];
  
  // Use API-enriched FAQs when available, otherwise static
  const faqItems = salaryData && dynamicFaqItems.length > 0 ? dynamicFaqItems : staticFaqItems;
  const faqSchema = generateFAQPageSchema(faqItems);
  
  // Always generate Organization schema (no API needed)
  const organizationSchema = generateOrganizationSchema(baseUrl);
  
  // Generate basic WebPage schema (no API needed)
  const webPageSchema = generateWebPageSchema(
    pageTitle,
    pageDescription,
    canonicalUrl,
    {
      occupation: occupationTitle,
      location: locationName,
      baseUrl,
    }
  );
  
  // Generate Occupation schema - always include, salary data is optional
  const occupationSchema = generateOccupationSchema(
    occupationTitle,
    locationName,
    salaryData ? {
      median: getMedianSalary() || 0,
      mean: getMeanSalary() || 0,
      min: percentileData[0]?.salary || getMeanSalary() * 0.6 || 0,
      max: percentileData[4]?.salary || getMeanSalary() * 1.3 || 0,
      p10: percentileData[0]?.salary,
      p25: percentileData[1]?.salary,
      p75: percentileData[3]?.salary,
      p90: percentileData[4]?.salary,
      currency: 'USD',
    } : undefined
  );
  
  // Combine all schemas - always include at least Org + WebPage + Occupation + FAQ
  const schemasToInclude = [organizationSchema, webPageSchema, occupationSchema, faqSchema];
  const structuredDataSchemas = generateMultipleSchemas(schemasToInclude);

  if (shouldRedirect) {
    return null;
  }

  if (isResolveError && !isResolvingOccupation && !currentOccupation) {
    return <NotFound />;
  }

  // Always render - don't wait for loading
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        occupation={occupationTitle}
        location={locationName}
        schemas={structuredDataSchemas}
        salaryData={salaryData ? {
          median: getMedianSalary(),
          mean: getMeanSalary(),
          min: percentileData[0]?.salary || getMeanSalary() * 0.6,
          max: percentileData[4]?.salary || getMeanSalary() * 1.3,
          currency: 'USD'
        } : undefined}
        canonical={canonicalUrl}
        noIndex={!isIndexable}
      />

      {/* Hero Section - Always render, use URL-derived title as fallback */}
      <PageHero
        badge={{ label: 'Occupation Salary Center' }}
        title={<>{occupationTitle} Average Salary in {locationName}</>}
        subtitle={
          meanForMeta > 0
            ? `${formatCurrency(meanForMeta)} average · ${formatCurrency(medianForMeta)} median · BLS OEWS ${dataYear}`
            : currentOccupation?.description
        }
      >
        {currentOccupation?.soc && (
          <p className="text-lg mb-8 -mt-4 text-white/70 flex items-center justify-center gap-2 animate-fade-in" style={{ animationDelay: '0.15s' }}>
            SOC Code: {currentOccupation.soc}
            <Link 
              to="/what-is-soc-code"
              className="inline-flex items-center gap-1 text-white/70 hover:text-white transition-colors text-sm"
              title="What is a SOC code?"
            >
              <HelpCircle className="h-4 w-4" />
              <span className="hidden sm:inline">What is this?</span>
            </Link>
          </p>
        )}

        {/* Top ad placement */}
        <AdFrame className="flex justify-center mb-8">
          <AdSlot slot="leaderboard" className="max-w-4xl" />
        </AdFrame>

        {salaryData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <HeroStatTile
              icon={DollarSign}
              value={formatCurrency(useRpp ? (salaryData.real_annual_mean_wage || salaryData.annual_mean_wage || salaryData.mean_salary || 0) : (salaryData.annual_mean_wage || salaryData.mean_salary || 0))}
              label="Mean Salary"
            />
            <HeroStatTile
              icon={Target}
              value={formatCurrency(useRpp ? (salaryData.real_annual_median_wage || salaryData.annual_median_wage || salaryData.median_salary || 0) : (salaryData.annual_median_wage || salaryData.median_salary || 0))}
              label="Median Salary"
            />
            <HeroStatTile
              icon={TrendingUp}
              value={`$${useRpp ? (salaryData.real_hourly_mean_wage || salaryData.hourly_mean_wage || salaryData.hourly_mean || 0) : (salaryData.hourly_mean_wage || salaryData.hourly_mean || 0)}/hr`}
              label="Hourly Rate"
            />
            <HeroStatTile
              icon={Users}
              value={formatNumber(salaryData.total_employment || salaryData.tot_emp || 0)}
              label="Total Jobs"
            />
          </div>
        )}

        {salaryData && (
          <div className="flex justify-center mt-4">
            <DataConfidenceBadge
              relativeStandardError={salaryData.mean_prse ?? null}
              employment={salaryData.total_employment || salaryData.tot_emp || null}
            />
          </div>
        )}

        {/* Broad-group occupations (e.g. Therapists) are employment-weighted
            aggregates of the detailed BLS occupations — disclose the mix. */}
        {Array.isArray(salaryData?.aggregated_from) &&
          salaryData.aggregated_from.length > 0 && (
            <p className="text-xs text-white/80 text-center mt-3 max-w-2xl mx-auto">
              Combined BLS estimate across:{' '}
              {(salaryData.aggregated_from as string[]).join(', ')}
            </p>
          )}
      </PageHero>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Occupation Introduction - Always render, uses URL-derived data */}
            <OccupationIntro 
              occupationTitle={occupationTitle}
              locationName={locationName}
              description={currentOccupation?.description}
              socCode={currentOccupation?.soc}
            />

            {/* Data-derived insight — unique per occupation × location */}
            {!isLoading && meanForMeta > 0 && (
              <SalaryPageInsight
                occupationTitle={occupationTitle}
                locationName={locationName}
                meanSalary={meanForMeta}
                medianSalary={medianForMeta}
                hourlyMean={typeof hourlyForMeta === 'number' ? hourlyForMeta : undefined}
                employment={employmentForMeta}
                p10={percentileData[0]?.salary}
                p90={percentileData[4]?.salary}
                dataYear={dataYear}
              />
            )}
            
            {/* Static SEO content - Always render, doesn't require API */}
            <StaticOccupationContent 
              occupationTitle={occupationTitle}
              locationName={locationName}
              socCode={occupationForContent?.soc || currentOccupation?.soc}
            />

            {/* Demand Signal - shows shortage/high-demand indicators */}
            <DemandSignalBlock
              socCode={occupationForContent?.soc || currentOccupation?.soc}
              occupationTitle={occupationTitle}
            />
            
            {/* Skeleton only while wage data is missing. Do not wait on
                useOccupationResolver — soc-map + salary snapshots already
                provide SOC/title/wages for prerender; blocking on the live
                api-occupations call left animate-pulse baked into ~800 pages. */}
            {isLoading && !salaryData && (
              <div className="space-y-6">
                <div className="h-32 bg-muted animate-pulse rounded" />
                <div className="h-96 bg-muted animate-pulse rounded" />
              </div>
            )}
            
            {/* Data-dependent content once wages + SOC (map or resolver) exist */}
            {!!salaryData && !!occupationForContent && (
              <>

                {/* What Do They Do Section */}
                <WhatDoTheyDo 
                  occupationTitle={occupationTitle}
                  description={occupationForContent?.description}
                  socCode={occupationForContent?.soc}
                />
                {/* Location Context */}
                <LocationContext 
                  locationName={locationName}
                  occupationTitle={occupationTitle}
                  medianSalary={getMedianSalary()}
                  meanSalary={getMeanSalary()}
                  employment={employmentForMeta}
                  p10={percentileData[0]?.salary}
                  p90={percentileData[4]?.salary}
                  hourlyMean={typeof hourlyForMeta === 'number' ? hourlyForMeta : undefined}
                  isNational={location === 'united-states' || location === 'national'}
                />

                {/* Cost of Living Note */}
                <CostOfLivingNote useRpp={useRpp} />

                {/* Real-wage (cost-of-living adjusted) insight — strongest
                    per-state differentiator. Renders unconditionally on state
                    pages using snapshot RPP data; on national pages there's
                    no RPP adjustment (RPP = 100) so the component returns null. */}
                {salaryData && (
                  <RealWageInsight
                    rppIndex={salaryData.rpp_all_items ?? null}
                    nominalMean={salaryData.annual_mean_wage ?? salaryData.mean_salary ?? null}
                    realMean={
                      salaryData.real_annual_mean_wage ??
                      (salaryData.rpp_all_items &&
                      (salaryData.annual_mean_wage || salaryData.mean_salary)
                        ? Math.round(
                            (salaryData.annual_mean_wage || salaryData.mean_salary) *
                              (100 / salaryData.rpp_all_items)
                          )
                        : null)
                    }
                    locationName={locationName}
                  />
                )}

                {/* State vs nation — unique location math beyond name-swapped shells */}
                {salaryData && !isNationalPage && (
                  <StateVsNationCompare
                    occupationTitle={occupationTitle}
                    occupationSlug={occupationSlug}
                    locationName={locationName}
                    stateMedian={getMedianSalary()}
                    stateMean={getMeanSalary()}
                    nationalMedian={
                      (salaryData.national_annual_median_wage as number | undefined) ??
                      (nationalCompare?.annual_median_wage as number | undefined) ??
                      null
                    }
                    nationalMean={
                      (salaryData.national_annual_mean_wage as number | undefined) ??
                      (nationalCompare?.annual_mean_wage as number | undefined) ??
                      null
                    }
                    stateEmployment={employmentForMeta}
                    nationalEmployment={
                      (salaryData.national_total_employment as number | undefined) ??
                      (nationalCompare?.total_employment as number | undefined) ??
                      null
                    }
                    stateRank={(salaryData.state_median_rank as number | undefined) ?? null}
                    stateCount={(salaryData.state_count as number | undefined) ?? null}
                    stateP10={percentileData[0]?.salary ?? salaryData.annual_10th_percentile_wage ?? null}
                    stateP90={percentileData[4]?.salary ?? salaryData.annual_90th_percentile_wage ?? null}
                  />
                )}
                {/* Financial status reality check - absolute income standing
                    (illustrative bands), complements OEWS percentiles above. */}
                {salaryData && (getMedianSalary() > 0 || getMeanSalary() > 0) && (
                  <FinancialStatusInsight
                    annualIncome={getMedianSalary() || getMeanSalary()}
                    contextLabel={`The ${getMedianSalary() > 0 ? 'median' : 'average'} salary for ${occupationTitle} in ${locationName}`}
                  />
                )}

                {/* Salary Overview */}
                <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-xl font-semibold">
                    Salary Overview
                  </CardTitle>
                  <RppToggle 
                    enabled={useRpp}
                    onToggle={setUseRpp}
                    disabled={areaCode === '99'}
                    disabledReason="United States is the national baseline (RPP = 100), so real and nominal wages match. Select a state to compare purchasing power."
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {salaryData && (
                    <PercentileChart
                      data={percentileData}
                      occupationMeanSalary={useRpp ? (salaryData.real_annual_mean_wage || salaryData.annual_mean_wage || salaryData.mean_salary || 0) : (salaryData.annual_mean_wage || salaryData.mean_salary || 0)}
                      title="Salary Distribution & Your Position"
                      className="col-span-full"
                      allowInput={true}
                      showOccupationBenchmark={true}
                    />
                  )}
                </div>
                
                {/* Premium CTA for career advancement */}
                <div className="mt-6 p-6 bg-gradient-to-r from-wage-accent/10 to-wage-accent/5 rounded-lg border border-wage-accent/20">
                  <div className="flex items-start gap-4">
                    <Briefcase className="h-6 w-6 text-wage-accent mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Advance Your Career</h3>
                      <p className="text-muted-foreground mb-4">
                        Ready to level up? Find courses, certifications, and coaching 
                        to boost your salary potential.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <AffiliateCta
                          provider="ziprecruiter"
                          occupation={occupationTitle}
                          location={locationName}
                          type="job-search"
                          className="bg-wage-accent hover:bg-wage-accent/90"
                        />
                        <AffiliateCta
                          provider="coursera"
                          occupation={occupationTitle}
                          location={locationName}
                          type="training"
                          className="bg-wage-accent hover:bg-wage-accent/90"
                        />
                        <AffiliateCta
                          provider="udemy"
                          occupation={occupationTitle}
                          location={locationName}
                          type="training"
                          className="border-wage-accent text-wage-accent hover:bg-wage-accent hover:text-white"
                        />
                        <AffiliateCta
                          provider="topresume"
                          occupation={occupationTitle}
                          location={locationName}
                          type="resume"
                          className="border-wage-accent text-wage-accent hover:bg-wage-accent hover:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* In-content ad */}
                <AdFrame className="my-6">
                  <AdSlot slot="in-content" />
                </AdFrame>
              </CardContent>
            </Card>

                {/* AI Risk Analysis Preview */}
                {currentOccupation?.soc && (
                  <AIRiskPreview
                    occupation={occupationTitle}
                    soc={currentOccupation.soc}
                    area={locationName}
                  />
                )}

                {/* Unique enriched content: O*NET skills/tasks, BLS growth
                    outlook, and layoffs signal. Differentiates this page from
                    raw BLS tables. Indexation is decided by curated list +
                    location only — never by enrichment success. */}
                {currentOccupation?.soc && (
                  <OccupationEnrichment
                    soc={currentOccupation.soc}
                    stateCode={enrichmentStateCode}
                    occupationTitle={occupationTitle}
                  />
                )}

                {/* Industry Breakdown */}
                {industryData && industryData.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Industries for {occupationTitle}</CardTitle>
                    </CardHeader>
                <CardContent>
                  <SalaryTable
                    data={industryData.map(item => ({
                      occ_title: item.industry_title || 'Unknown Industry',
                      soc: item.naics_code || '',
                      annual_mean_wage: item.mean_salary || 0,
                      annual_median_wage: item.median_salary || 0,
                      hourly_mean_wage: item.hourly_mean || 0,
                      total_employment: item.employment_count || 0,
                      mean_salary: item.mean_salary || 0,
                      median_salary: item.median_salary || 0,
                      hourly_mean: item.hourly_mean || 0,
                      tot_emp: item.employment_count || 0,
                      jobs_1000: item.jobs_per_1000 || 0
                    }))}
                    showAffiliateEvery={5}
                    area={location || ''}
                    className="shadow-none border-0"
                    firstColumnLabel="Industry"
                  />
                    </CardContent>
                  </Card>
                )}

                {/* Related Occupations */}
                {careerRecommendations.length > 0 && recommendedSalaryData.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Related Occupations</CardTitle>
                    </CardHeader>
                <CardContent>
                  <SalaryTable
                    data={careerRecommendations.map(rec => {
                      const salaryData = recommendedSalaryData.find(s => s.soc === rec.recommended_occupation_code);
                      return {
                        occ_title: rec.occupation_title,
                        soc: rec.recommended_occupation_code,
                        annual_mean_wage: salaryData?.mean_salary || 0,
                        annual_median_wage: salaryData?.median_salary || 0,
                        hourly_mean_wage: salaryData?.hourly_mean || 0,
                        total_employment: salaryData?.total_employment || 0,
                        mean_salary: salaryData?.mean_salary || 0,
                        median_salary: salaryData?.median_salary || 0,
                        hourly_mean: salaryData?.hourly_mean || 0,
                        tot_emp: salaryData?.total_employment || 0,
                        recommendation_score: rec.recommendation_score
                      };
                    })}
                    showAffiliateEvery={4}
                    area={location || ''}
                    className="shadow-none border-0"
                  />
                    </CardContent>
                  </Card>
                )}

                {/* SEO Internal Linking Components */}
                <RelatedOccupations
                  currentOccupation={{
                    title: occupationTitle,
                    soc: currentOccupation?.soc || '',
                    slug: occupationSlug
                  }}
                  currentLocation={location}
                  relatedOccupations={careerRecommendations.slice(0, 5).map(rec => ({
                    title: rec.occupation_title || '',
                    soc: rec.recommended_occupation_code || '',
                    slug: rec.occupation_title?.toLowerCase().replace(/\s+/g, '-') || ''
                  }))}
                />

                <RelatedStates
                  occupationSlug={occupationSlug}
                  currentState={location}
                />

                <OccupationHubLinks
                  occupationSlug={occupationSlug}
                  location={location}
                  socCode={occupationForContent?.soc || currentOccupation?.soc}
                />

                {/* Career Advancement Strategy — uses api-career-insights fields */}
                {hasAdvancementContent && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Career Advancement Strategy</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {salaryBoosters.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium mb-4">Salary Boost Strategies</h3>
                          <div className="grid gap-4">
                            {salaryBoosters.slice(0, 3).map((booster: {
                              strategy: string;
                              target_industry?: string;
                              current_avg_salary?: number;
                              target_avg_salary?: number;
                              improvement_potential: number;
                              difficulty: string;
                            }, index: number) => (
                              <div
                                key={index}
                                className="flex items-start gap-4 p-4 border rounded-lg bg-card"
                              >
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-semibold">
                                  {index + 1}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                    <h4 className="text-lg font-medium">{booster.strategy}</h4>
                                    <div className="flex items-center gap-2">
                                      <Badge className={getDifficultyColor(booster.difficulty)}>
                                        {booster.difficulty}
                                      </Badge>
                                      <span className={`font-semibold ${getSalaryImprovementColor(booster.improvement_potential)}`}>
                                        {formatSalaryImprovement(booster.improvement_potential)}
                                      </span>
                                    </div>
                                  </div>
                                  {booster.target_industry && (
                                    <p className="text-sm text-muted-foreground mb-2">
                                      Focus on {booster.target_industry}
                                    </p>
                                  )}
                                  {booster.current_avg_salary != null && booster.target_avg_salary != null && (
                                    <p className="text-sm text-muted-foreground">
                                      {formatCurrency(booster.current_avg_salary)} → {formatCurrency(booster.target_avg_salary)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {skillRecommendations.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium mb-4">Skills to Develop</h3>
                          <div className="grid gap-2">
                            {skillRecommendations.slice(0, 6).map((skill: {
                              name: string;
                              importance_score?: number;
                              reason?: string;
                              salary_impact?: string;
                              category?: string;
                            }, index: number) => (
                              <div
                                key={index}
                                className="flex flex-col gap-2 p-3 border rounded-lg sm:flex-row sm:items-start sm:justify-between sm:gap-3"
                              >
                                <div className="min-w-0 flex-1">
                                  <span className="font-medium">{skill.name}</span>
                                  {(skill.reason || skill.salary_impact) && (
                                    <p className="text-sm text-muted-foreground mt-0.5">
                                      {skill.reason || skill.salary_impact}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
                                  {skill.category && (
                                    <Badge variant="outline" className="whitespace-nowrap">
                                      {skill.category}
                                    </Badge>
                                  )}
                                  {skill.importance_score != null && (
                                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                                      {Math.round(skill.importance_score)} importance
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Related Career Opportunities */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Explore Similar Careers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {careerRecommendations.slice(0, 6).map((rec, index) => (
                  <div key={index} className="group">
                    <OccupationLink
                      occupation={{
                        title: rec.occupation_title,
                        soc: rec.recommended_occupation_code,
                        slug: rec.occupation_title?.toLowerCase().replace(/\s+/g, '-')
                      }}
                      location={location}
                      className="text-sm hover:text-primary group-hover:underline"
                    />
                    <div className="text-xs text-muted-foreground mt-1">
                      Score: {Math.round(rec.recommendation_score)}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <MetricNavigation
              occupation={currentOccupation}
              location={location}
              currentMetric="salary"
              collapsible={false}
            />
            
            <RelatedMetrics
              occupation={currentOccupation}
              location={location}
              currentMetric="salary"
              variant="compact"
            />

            {/* Similar careers - optional pgvector AI layer (hidden until provisioned) */}
            <SimilarCareers
              soc={currentOccupation?.soc}
              location={location || 'united-states'}
            />

            {/* Bottom sidebar ad */}
            <div className="sticky top-24">
              <AdSlot slot="sidebar" />
            </div>

            {/* Newsletter Signup */}
            <NewsletterSignup 
              source="occupation_salary_page"
              variant="compact"
              title="Get Salary Updates for This Occupation"
              description="Stay informed about salary trends and career opportunities."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationSalaryCenter;